import { useState, useRef, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import providersService from '../services/providers';
import metricsService from '../services/metrics';
import { rumService } from '../services/rum';
import { IMPRESSIONS } from '../constants/metrics';
import { IdPs } from '../constants/providers';
import { AppConfig } from '../types/app';
import { IdProvider } from '../types/auth';
import { resolveIdProvider } from './resolveIdProvider';
import { providerMessages } from '../i18n/provider.messages';
import messages from '../i18n/chooseSignInMethod.messages';
import styles from './ChooseSignInMethod.module.css';

interface ChooseSignInMethodProps {
  config: AppConfig;
  email: string;
  loginMethods: string[];
  displayNames?: Record<string, string>;
  /**
   * Resolves an IDP name to a full provider (including any non-main pool
   * details). Defaults to {@link resolveIdProvider}; the parent may pass a
   * cache-aware resolver to avoid a duplicate GetPublicIdpDetails call when the
   * provider was already resolved for its display name.
   */
  resolveProvider?: (idpName: string) => Promise<IdProvider>;
  onChangeEmail: () => void;
  onRedirectToIdp: (provider: IdProvider, loginHint?: string) => void;
}

const IDP_DESCRIPTION_KEYS: Record<string, keyof typeof messages> = {
  AWSBuilderID: 'awsBuilderIdDescription',
  LoginWithAmazon: 'loginWithAmazonDescription',
  AmazonFederate: 'amazonFederateDescription',
  AmazonSystemFederate: 'amazonFederateDescription',
  OTP: 'otpDescription',
};

function ChooseSignInMethod({
  config,
  email,
  loginMethods,
  displayNames = {},
  resolveProvider = (idpName: string) => resolveIdProvider(config, idpName),
  onChangeEmail,
  onRedirectToIdp,
}: ChooseSignInMethodProps) {
  const intl = useIntl();
  const [selectedMethod, setSelectedMethod] = useState<string | undefined>();
  const [isResolving, setIsResolving] = useState(false);
  const metricsPublisher = useRef(
    metricsService.getPublisher('ChooseSignInMethodPage')
  );
  // OAuth client_id from the request URL, tagged onto the simplified-login RUM
  // events so dashboards can break the funnel down by app client (matches
  // SimplifiedSignIn.tsx / AuthResponse.tsx).
  const clientId =
    new URLSearchParams(window.location.search).get('client_id') || 'NA';

  useEffect(() => {
    metricsPublisher.current.publishCounterMonitor(IMPRESSIONS, 1);
    rumService.recordEvent('ChooseSignInMethodImpression', {
      loginMethods: loginMethods.join(','),
      clientId,
      url: window.location.href,
    });
  }, [loginMethods, clientId]);

  const handleContinue = () => {
    if (!selectedMethod || isResolving) return;
    setIsResolving(true);
    metricsPublisher.current.publishCounterMonitor('MethodSelected', 1);
    rumService.recordEvent('ChooseSignInMethodSelected', {
      provider: selectedMethod,
      clientId,
      url: window.location.href,
      userAgent: navigator.userAgent,
    });
    // Also emit SSORedirect so email flows that resolve to multiple methods
    // (and are completed via the chooser) are counted in the same redirect
    // denominator as single-method email flows. Without this, the by-IdP /
    // Builder ID email conversion rate is inflated (conversions counted in the
    // numerator but the chooser redirect missing from the denominator).
    rumService.recordEvent('SimplifiedSignInSSORedirect', {
      idp: selectedMethod,
      provider: selectedMethod,
      clientId,
      url: window.location.href,
      userAgent: navigator.userAgent,
      path: `${window.location.pathname}${window.location.search}`,
    });
    // Flush batched RUM events before the redirect navigates the page away,
    // mirroring handleSocialSignIn. onRedirectToIdp -> startAuthFlow triggers a
    // federated-sign-in navigation, so without this the ChooseSignInMethodSelected
    // and SimplifiedSignInSSORedirect events risk being dropped — under-counting
    // the SSORedirect denominator and inflating the by-IdP conversion rate.
    rumService.dispatchBeacon();
    if (selectedMethod === IdPs.OTP) {
      sessionStorage.setItem('otp_prefill_email', email);
    }
    // Non-standard (ESSO/enterprise) IDPs may live in a non-main Cognito user
    // pool, so resolve full IDP details (userPoolId/clientId/idpType) before
    // redirecting. Standard providers resolve synchronously to a main-pool
    // provider inside resolveIdProvider. resolveProvider may be a cache-aware
    // resolver supplied by the parent to avoid a duplicate lookup.
    resolveProvider(selectedMethod)
      .then((provider) => {
        onRedirectToIdp(provider, email);
      })
      .catch(() => {
        const fallback = providersService.transformNameToIdProvider(
          selectedMethod,
          config.gandalfDomain
        );
        onRedirectToIdp(fallback, email);
      })
      .finally(() => setIsResolving(false));
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.welcomeBack}>
        <FormattedMessage {...messages.welcomeBack} />
      </h1>
      <p className={styles.subtitle}>
        <span className={styles.subtitleLight}>
          <FormattedMessage
            {...messages.signingInWith}
            values={{
              email: <span className={styles.subtitleBold}>{email}</span>,
            }}
          />
        </span>
        <button
          type="button"
          className={styles.changeEmailLink}
          onClick={onChangeEmail}
          data-testid="choose-method-change-email"
        >
          <FormattedMessage {...messages.changeEmail} />
        </button>
      </p>

      <div className={styles.card}>
        <h2 className={styles.header}>
          <FormattedMessage {...messages.header} />
        </h2>

        <div
          className={styles.tiles}
          role="radiogroup"
          aria-label={intl.formatMessage(messages.header)}
        >
          {loginMethods.map((method) => (
            <label
              key={method}
              htmlFor={`signInMethod-${method}`}
              aria-label={
                providerMessages[method]
                  ? intl.formatMessage(providerMessages[method])
                  : displayNames[method] || method
              }
              className={`${styles.tile} ${
                selectedMethod === method ? styles.tileSelected : ''
              }`}
            >
              <input
                id={`signInMethod-${method}`}
                type="radio"
                name="signInMethod"
                value={method}
                checked={selectedMethod === method}
                onChange={() => setSelectedMethod(method)}
                className={styles.radioInput}
              />
              <span className={styles.radioCircle} aria-hidden="true" />
              <div className={styles.tileContent}>
                <span className={styles.tileLabel}>
                  {providerMessages[method]
                    ? intl.formatMessage(providerMessages[method])
                    : displayNames[method] || method}
                </span>
                <span className={styles.tileDescription}>
                  {IDP_DESCRIPTION_KEYS[method] ? (
                    <FormattedMessage
                      {...messages[IDP_DESCRIPTION_KEYS[method]]}
                    />
                  ) : (
                    intl.formatMessage(messages.organizationalSsoDescription)
                  )}
                </span>
              </div>
            </label>
          ))}
        </div>

        <button
          type="button"
          className={styles.primaryButton}
          onClick={handleContinue}
          disabled={!selectedMethod || isResolving}
          data-testid="choose-method-continue"
        >
          <FormattedMessage {...messages.continueButton} />
        </button>
      </div>
    </div>
  );
}

export default ChooseSignInMethod;
